import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FIELD_MAX } from "@/lib/fieldLimits";

const TYPES = ["Onboarding", "Approval", "Canceled", "Declined"] as const;
type NotifType = (typeof TYPES)[number];

interface Recipient {
  email: string;
  types: NotifType[];
}

function parse(value: string): Recipient[] {
  if (!value.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (r): r is { email: unknown; types: unknown } =>
          !!r && typeof r === "object" && "email" in r && "types" in r,
      )
      .map((r) => ({
        email: typeof r.email === "string" ? r.email : "",
        types: Array.isArray(r.types)
          ? r.types.filter((t): t is NotifType => TYPES.includes(t as NotifType))
          : [],
      }));
  } catch {
    return [];
  }
}

export default function RecipientsField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const rows = useMemo(() => parse(value), [value]);

  function commit(next: Recipient[]) {

    onChange(next.length ? JSON.stringify(next) : "");
  }

  function updateRow(i: number, patch: Partial<Recipient>) {
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function toggleType(i: number, t: NotifType) {
    const has = rows[i].types.includes(t);
    updateRow(i, {
      types: has ? rows[i].types.filter((x) => x !== t) : [...rows[i].types, t],
    });
  }

  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <p className="text-xs text-slate-400">
          No recipients configured — application notifications are disabled.
        </p>
      )}

      {rows.map((r, i) => (
        <div
          key={i}
          className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50"
        >
          <Input
            type="email"
            value={r.email}
            placeholder="name@example.com"
            onChange={(e) => updateRow(i, { email: e.target.value })}
            maxLength={FIELD_MAX.setting.recipientEmail}
            className="flex-1 min-w-[220px]"
            autoComplete="off"
            spellCheck={false}
          />
          <div className="flex items-center gap-3">
            {TYPES.map((t) => (
              <label key={t} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <Checkbox checked={r.types.includes(t)} onCheckedChange={() => toggleType(i, t)} />
                {t}
              </label>
            ))}
          </div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-destructive"
            onClick={() => commit(rows.filter((_, idx) => idx !== i))}
          >
            Remove
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => commit([...rows, { email: "", types: [...TYPES] }])}
      >
        Add recipient
      </Button>
    </div>
  );
}
